// Domain model for 4sum. Pure data — no React/RN imports anywhere in this file.

export type HoleInfo = {
  par: number;
  /** 0 means unknown — a directory record with pars and stroke indexes but no per-hole
   *  yardage column (see opengolf.ts's mapOpenGolfDetail) still imports rather than getting
   *  dropped whole. Every renderer of a per-hole yardage treats 0 as "no data" and shows a
   *  dash instead of "0y" — never a real distance. */
  yardage: number;
  /** Stroke index (handicap ranking) 1–18; 1 = hardest hole. */
  strokeIndex: number;
};

export type Tee = {
  id: string;
  name: string; // "Blue", "White", ...
  gender: 'M' | 'F' | 'U';
  rating: number; // course rating for this tee, e.g. 71.4
  slope: number; // slope rating, e.g. 128
  yardage: number; // total yards
  holes: HoleInfo[]; // length 18 (or 9 for nine-hole courses)
  /** Set when `rating` was not in the directory record and was filled in as this tee's own
   *  par (course_rating null → rating = par, the standard fallback: it zeroes the
   *  rating-minus-par term in courseHandicap, leaving index × slope/113). Absent — never
   *  `false` — on a tee with a real rating. */
  ratingEstimated?: boolean;
  /** Set on a tee built from OSM centreline geometry (osmCourse.ts) rather than a surveyed
   *  scorecard — every `HoleInfo.yardage` on it is a straight tee-to-green estimate, not a
   *  measured distance. Shown as "yardages estimated from the map" next to the existing
   *  "rating estimated" note. Absent — never `false` — on a tee with real yardages. */
  yardagesEstimated?: boolean;
};

/** A WGS-84 point. Degrees, the way every phone and every mapping API reports them. */
export type LatLng = { lat: number; lng: number };

/**
 * Surveyed points on one hole, for the GPS readouts.
 *
 * Green coordinates hang off the COURSE, not the tee, because every tee plays to the same
 * green — putting them on Tee would store the same three points four times and let them
 * drift apart. Tee markers are the opposite case, so those are keyed by `Tee.id`.
 *
 * Every field is optional at every level, and that is the whole design. Not one of the 196
 * seeded courses has a single coordinate, so a hole with no geo has to be the normal case
 * that costs nothing, not an error state — the distance readout hides itself and the drive
 * marker falls back to marking both ends by hand.
 */
/**
 * Anything on a hole worth knowing the distance to, other than the green.
 *
 * The bunker you always find, the creek at 240, the spot you lay up to. Marked by standing
 * on it once — the same crowd-sourced deal as the greens, because 4sum licenses no course
 * geometry and so has no other way to know a hazard exists.
 */
export type HolePoint = {
  id: string;
  /** What the player called it. Free text, theirs, shown verbatim. */
  label: string;
  at: LatLng;
};

export type HoleGeo = {
  /** Front, middle and back of the green. Any subset; the readout shows what it has. */
  green?: { front?: LatLng; center?: LatLng; back?: LatLng };
  /** Tee markers keyed by `Tee.id` — a drive is measured from the tee actually played. */
  tees?: Record<string, LatLng>;
  /** Hazards and layup targets, in the order they were marked. */
  points?: HolePoint[];
};

/**
 * Which points in the matching `HoleGeo` came from the shared consensus
 * (`course_geo_consensus`, see supabase/migrations/0017_course_geo_shared.sql and
 * src/sync/courseGeoShare.ts) rather than this phone's own hand mark or OSM import.
 *
 * ADDITIVE AND OPTIONAL BY DESIGN. A `courseGeo` overlay persisted by an app build from
 * before this shipped has no matching `courseGeoProvenance` entry at all — every key here is
 * read as "absent", which means "own point, never touched by a consensus refresh", so a
 * phone that already walked a course and marked it keeps every one of those marks exactly as
 * it always has, with nothing to migrate. Only a point this phone itself downloaded from
 * consensus (`store.applyConsensusGeo`, see src/sync/bridge.ts's `fetchAndApplyConsensus`)
 * is ever marked `true` here — and the moment a person's own thumb overwrites that slot
 * (`setGreenPoint`, `setTeePoint`), the flag is cleared back to "own" so their mark can never
 * be silently reverted or removed by a later consensus download. `true` is the only value
 * ever stored — there is no `false`, the same "absence is the default" convention
 * `HoleGeo`'s own fields use.
 */
export type HoleGeoProvenance = {
  green?: Partial<Record<'front' | 'center' | 'back', true>>;
  tees?: Record<string, true>;
  /** Keyed by `HolePoint.id` — consensus points carry a deterministic synthesized id
   *  (`consensus:<holeIdx>:<label>`, see `consensusToHoleGeo`), so the same consensus point
   *  keeps the same id across repeated downloads. */
  points?: Record<string, true>;
};

export type Course = {
  id: string;
  name: string;
  city: string;
  state: string; // "IL", "WI"
  tees: Tee[];
  isCustom?: boolean;
  /**
   * Per-hole coordinates, indexed like a tee's `holes` array (18, or 9 on a nine-holer).
   * Absent on every seeded course — it is filled in by walking a course once with
   * CourseGeoScreen, or left empty forever without breaking anything.
   */
  holeGeo?: HoleGeo[];
  /** Where the clubhouse is. Set on courses imported from OpenGolfAPI; absent on the bundled
   *  seed. Lets the green finder query the map without a second lookup. */
  location?: LatLng;
  /** Which directory this course was imported from, when it was. Absent on hand-made custom
   *  courses. 'osm' is a course built straight from OpenStreetMap map data by osmCourse.ts —
   *  no OpenGolfAPI directory record exists for it at all, which is the whole reason it went
   *  through that path instead of opengolf.ts's normal import. */
  source?: 'opengolfapi' | 'osm';
  sourceId?: string;
  /** Star rating and review count from the course directory, when it had one. */
  reviews?: { rating: number; count: number };
  /** Set on an osmCourse.ts course whose hole chain came up short — OSM never drew every
   *  numbered hole for this course. Each entry is a display label ("Hole 16"); the missing
   *  holes themselves sit in `tees[].holes` at par 4 / yardage 0 (see HoleInfo.yardage) and
   *  the course editor is the intended fix. Absent on every other course. */
  incomplete?: string[];
};

/** Persistent roster entry. Never hard-deleted (history references it); archive instead. */
export type Player = {
  id: string;
  name: string;
  initials: string; // 1–3 chars shown in avatars
  handicapIndex: number | null; // player's handicap, e.g. 12.4; null = no handicap (treated as 0)
  colorKey: string; // key into theme.playerColors
  archived?: boolean;
  /** Venmo username, stored bare — no @, no URL. Optional: plenty of groups settle in cash,
   *  and a player without one simply shows no Venmo action on Settle Up. A handle is all
   *  this holds. The app stores no payment method and never moves money. */
  venmo?: string;
  /** Other names this player has been known by — a hosted join matched under a first name
   *  ("Chris") that a merge folded into this record, or a name confirmed as the same person
   *  on a later join. Read by `planRoster` (src/sync/hosted.ts) as an extra name to match an
   *  entrant against, alongside `name` itself. Absent on a player nobody has ever merged or
   *  confirmed an alias for. */
  aliases?: string[];
};

export type JunkFlag = 'greenie' | 'sandie' | 'barkie' | 'polie' | 'arnie';
export type BBBEvent = 'bingo' | 'bango' | 'bongo';
export type LRZone = 'left' | 'center' | 'right';

/**
 * A side contest the HOST puts on a hole — the sign on the tee box at an outing.
 *
 * Distinct from `HoleData.proxy` and from the `greenie` junk flag, both of which are things
 * a group records for its own betting game. A contest is declared before anyone tees off,
 * applies to the whole field, and is the reason a player wants to know which holes have
 * something on them *before* they get there.
 */
export type ContestKind = 'ctp' | 'longDrive' | 'longPutt' | 'straightestDrive';

export type HoleContest = {
  kind: ContestKind;
  /** Replaces the default label — "Closest to the pin, sponsored by Gurnee Ford". */
  label?: string;
  /** Restricts who can win it (a women's long drive). Absent means the whole field. */
  eligiblePlayerIds?: string[];
};

/** Who took a hole's contest. `measure` is display-only text, exactly as it was measured. */
export type ContestResult = {
  kind: ContestKind;
  playerId: string;
  /** "14 ft 3 in", "287 yds" — never parsed, only shown. */
  measure?: string;
};

export type PlayerHoleScore = {
  gross: number | null; // null = not yet entered
  putts: number | null;
};

/**
 * One measured drive.
 *
 * `yards` is the only field the engine reads, and it is stored rather than derived so a
 * drive measured on the course keeps its number forever — re-deriving it later would mean
 * depending on coordinates that a player can edit, or that were never captured at all.
 *
 * `from`/`to` are kept anyway, so a mark made by mistake can be inspected and re-measured
 * instead of just deleted, and so a future shot map has something to draw.
 */
export type DriveMark = {
  /** Tee to where it finished, in whole yards. Carry plus roll — what a group argues about. */
  yards: number;
  /**
   * Whether it finished in the fairway. Only read when a game is set to fairway-only, and
   * absent means nobody was asked — which is not the same as `false`, and is why the engine
   * treats an absent value as ineligible only when the rule is switched on.
   */
  fairway?: boolean;
  /** Where it was measured from and to, when it was measured by GPS rather than typed. */
  from?: LatLng;
  to?: LatLng;
  /** How the number got here. A typed number is still a legitimate mark. */
  source: 'gps' | 'manual';
};

/** Everything the scorekeeper can record on one hole. */
export type HoleData = {
  /** keyed by playerId */
  scores: Record<string, PlayerHoleScore>;
  junk: Record<string, JunkFlag[]>;
  bbb: Record<string, BBBEvent[]>;
  wolf?: {
    wolfId: string;
    partnerId: string | null; // null while unpicked or when lone
    lone: boolean;
    blind: boolean;
  };
  banker?: {
    bankerId: string;
    /** wager in cents, keyed by opponent playerId */
    wagers: Record<string, number>;
  };
  leftRight?: {
    zones: Record<string, LRZone>;
  };
  /**
   * Measured drives on this hole, keyed by playerId.
   *
   * Sparse on purpose, at both levels. A hole with no entry was simply never measured, and
   * a player with no entry on a measured hole did not have their drive marked — neither is
   * an error, because Longest Drive can be added to a round on the seventh tee and used on
   * whichever holes the group feels like. Every reader has to tolerate the gaps.
   */
  drives?: Record<string, DriveMark>;
  /**
   * Closest to the pin in regulation — one winner per hole, or absent if nobody hit it
   * (or nobody bothered to record it).
   *
   * Not the same thing as the `greenie` junk flag: a greenie is a par-3 tee shot that
   * finishes on the green and only Dots & Junk reads it. A proxy is a scoring category
   * in its own right — Scotch spends a point on it, Wolf can run it as a side bet.
   */
  proxy?: { playerId: string };
  /**
   * Who takes the snake when more than one player three-putts this green.
   *
   * The real rule is whoever putted last, which the app has no way of knowing — so on the
   * rare hole where it is ambiguous, the scorekeeper says. Absent means nobody had to
   * choose, and the engine falls back to group order.
   */
  snakeTaker?: string;
  /** Manual Nassau presses declared ON this hole (apply from this hole onward). side: 0|1 into config.teams / participants. */
  presses?: Array<{ gameId: string; side: 0 | 1 }>;
  /** Winners of the host's contests on this hole. See Round.contests. */
  contestResults?: ContestResult[];
  /** Balls lost or hit out of bounds, per player. Absent means none. */
  ballsLost?: Record<string, number>;
};

export type RoundPlayer = {
  playerId: string;
  teeId: string;
  /** Course handicap, frozen at round start (recomputed if tee/index edited during setup). */
  courseHandicap: number;
};

/** Which holes this round plays. */
export type HoleSet = 'full' | 'front' | 'back';

export type Round = {
  id: string;
  courseId: string;
  startedAt: number; // epoch ms
  finishedAt?: number;
  status: 'active' | 'finished';
  players: RoundPlayer[]; // 1–8; engines validate their own min/max
  holes: HoleData[]; // same length as the PLAYED hole set, not always the tee's full array
  games: import('./engine/types').GameInstance[];
  currentHole: number; // 0-based, within the played hole set
  /** Optional for backwards compatibility: a round stored without it played all 18. */
  holeSet?: HoleSet;
  /**
   * Shotgun start: the hole this group teed off on, 0-based within the played hole set.
   *
   * Absent or 0 means the ordinary case — everyone starts on the first hole. At an outing
   * every group starts at once on a different hole and wraps around, so a group starting
   * here plays startHole, startHole+1, … and comes back round to startHole-1. It changes
   * the ORDER holes are played, never which holes or what they are worth.
   */
  startHole?: number;
  /**
   * Contests the host has put on holes, keyed by 0-based hole index within the played set.
   *
   * Copied down from the tournament when the round is created — the same pattern the game
   * templates use — so a round is self-contained and scores without reaching for its parent.
   */
  contests?: Record<number, HoleContest[]>;
  /** Set when this round is part of a Tournament. A tournament's rounds are DERIVED by
   *  filtering on this — there is no roundIds array to keep in sync. */
  tournamentId?: string;
};

/** A player entered in a tournament's field. */
export type TournamentPlayer = {
  playerId: string;
  /** Index snapshotted when the player was entered; the roster entry can drift after. */
  handicapIndex: number | null;
};

/**
 * A game attached to the tournament rather than to one round.
 *
 * Its `id` is the link that aggregates money across rounds: every round created under the
 * tournament gets a GameInstance stamped with this id as its `templateId`, so three rounds
 * of Skins read as one line on a player's breakdown instead of three unrelated games.
 */
export type TournamentGame = { id: string; config: import('./engine/types').GameConfig };

/**
 * A container of rounds plus a field and a set of game templates.
 *
 * Deliberately covers both shapes golfers actually play: a one-day outing where a 12-person
 * field splits into three foursomes (each group its own round, every player in exactly one),
 * and a multi-day series where the same field plays every round. Standings sum whatever
 * rounds a player appears in, so neither shape needs special-casing.
 */
export type Tournament = {
  id: string;
  name: string;
  /** Default course for new rounds, and the context used to validate the game templates. */
  courseId: string;
  defaultHoleSet: HoleSet;
  startedAt: number;
  finishedAt?: number;
  status: 'active' | 'finished';
  field: TournamentPlayer[];
  games: TournamentGame[];
  /**
   * The host's contests, keyed by 0-based hole index — the declaration for the whole event.
   * Copied into each round at creation; see Round.contests.
   */
  contests?: Record<number, HoleContest[]>;
};

export const emptyHole = (playerIds: string[]): HoleData => ({
  scores: Object.fromEntries(playerIds.map((id) => [id, { gross: null, putts: null }])),
  junk: Object.fromEntries(playerIds.map((id) => [id, []])),
  bbb: Object.fromEntries(playerIds.map((id) => [id, []])),
});
